<template>
	<div class="container">
		<div class="container__header">
			<h1 v-if="result">{{ $t('Výsledok č.') }} {{ result.result_id }} - {{ result.lab_id }}</h1>
			<div v-else class="skelet w-[200px] h-[34px]"></div>

			<div class="container__header__right">
				<Z-icon-button :icon="icons.download" class="container__header__right__btn" :disabled="isPrintDownloadDisabled" :loading="loadingPdfDownload" @click="onCopyPrintResult('pdf', 'download')">{{ $t('Stiahnuť PDF') }}</Z-icon-button>
				<Z-icon-button :icon="icons.printer" class="container__header__right__btn" :disabled="isPrintDownloadDisabled" :loading="loadingPdfOpen" @click="onCopyPrintResult('pdf', 'open')">{{ $t('Vytlačiť') }}</Z-icon-button>

				<A-dropdown :options="copyOptions" :disabled="isPrintDownloadDisabled" :loading="loadingCopy" />
			</div>
		</div>

		<div class="container__table">
			<table v-if="!parameters || result.params?.length">
				<thead>
					<tr>
						<th>{{ $t('Parameter') }}</th>
						<th>{{ $t('Hodnota') }}</th>
						<th>{{ $t('Jednotka') }}</th>
						<th class="whitespace-nowrap">{{ $t('Ref. Hodnota') }}</th>
						<th class="approval">{{ $t('Schválenie') }}</th>
						<th class="right">{{ $t('História') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(parameter, index) in $skelet(parameters, 3)" :key="`result-${index}`">
						<td>
							<template v-if="parameter">
								<span class="dotted-underline mr-[8px] pt-1 cursor-pointer" :class="{ 'text-[#CB2929] border-[#CB2929]': isCritical(parameter?.value.value, parameter?.ref_value_min, parameter?.ref_value_max) }" v-tooltip.top-center="{ theme: 'info-tooltip', content: parameter?.short_name }">
									{{ parameter?.name }}
								</span>

								<!-- <ion-icon color="danger" class="h-[16px] mb-[-3px]" v-if="!parameter?.value || parameter?.value?.value === '... '" :src="icons.waiting" /> -->
							</template>

							<div v-else class="skelet w-[80px] h-[22px]"></div>
						</td>

						<td>
							<template v-if="parameter">
								<div v-if="parameter?.value">
									<div v-if="parameter.value?.type == 'TEXT'" v-html="dompurify(formatParameterValueRichText(parameter.value.value))"></div>
									<template v-else>
										<div :class="{ 'font-bold text-[#CB2929]': isCritical(parameter?.value?.value, parameter?.reference_interval?.low, parameter?.reference_interval?.high) }">
											{{ parameter.value.value }}
										</div>
									</template>
								</div>

								<div v-else>-</div>
							</template>

							<div v-else class="skelet w-[20px] h-[22px]"></div>
						</td>
						<td>
							<template v-if="parameter">
								{{ parameter?.unit }}
							</template>

							<div v-else class="skelet w-[40px] h-[22px]"></div>
						</td>
						<td>
							<template v-if="parameter">
								<span v-if="parameter.reference_interval?.low && parameter.reference_interval?.high"> {{ parameter.reference_interval?.low }} - {{ parameter.reference_interval?.high }} </span>

								<span v-else>-</span>
							</template>

							<div v-else class="skelet w-[30px] h-[22px]"></div>
						</td>
						<td class="approval">
							<div v-if="parameter" class="leading-[20px]">{{ parameter.validate?.person }}</div>
							<div v-else class="skelet w-[70px] h-[22px]"></div>
							<!-- <div v-if="parameter.approval.datetime" class="leading-[20px] text-[#6E7D8D]">{{ formatDate(parameter.approval.datetime) + $t(" o ") + formatTime(parameter.approval.datetime) }}</div> -->
						</td>
						<td class="right">
							<button :disabled="!parameter || parameter?._disabled_history" :class="{ 'opacity-50 cursor-not-allowed': !parameter || parameter?._disabled_history }" v-tooltip.top-center="{ content: parameter?._disabled_history ? $t('Pre tento parameter nie je možné zobraziť históriu') : null, theme: 'info-tooltip' }" @click="showParameterHistory(parameter)">
								<ion-icon color="primary" :src="icons.history" />
							</button>
						</td>
					</tr>
				</tbody>
			</table>
			<div class="no-results" v-else>
				<p>{{ $t('Tento výsledok zatiaľ neobsahuje žiadne parametre') }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import { Clipboard } from '@capacitor/clipboard'
import MdxParameterModal from '@/plugins/refactor/components/parameter-modal'
import { printer, download, history, waiting } from '@/plugins/@medirex/icons'
import { formatTime, formatDate } from '@/plugins/app/utils/date.utils'
import ADropdown from './a-dropdown.vue'
import { confirmModalController } from '@medirex/vue-components'
import { copy } from '@/plugins/@medirex/icons'

import copyTable from '../_assets/copy-table.png'
import copyText from '../_assets/copy-text.png'

import CopyPrintMixin from '../../_mixins/copy-print.mixin'
import { dompurify } from '@/plugins/app/_config/modules/global-properties'

export default {
	mixins: [CopyPrintMixin],
	components: { ADropdown },

	props: {
		request: {
			type: [Object, null],
			required: true
		},

		result: {
			type: [Object, null],
			required: true
		},

		patient: {
			type: [Object, null],
			default: null
		},

		orderExternalId: {
			type: [String, null],
			default: null
		}
	},

	setup() {
		return {
			dompurify,
			icons: {
				printer,
				download,
				history,
				waiting
			}
		}
	},

	data() {
		return {
			formatDate,
			formatTime,
			loadingCopy: false,
			loadingPdfOpen: false,
			loadingPdfDownload: false,
			copyOptions: [
				{
					title: this.$t('Kopírovať v tabuľke'),
					description: this.$t('Text bude po vložení v tabuľke.'),
					image: copyTable,
					handler: () => {
						this.onCopyPrintResult('txt')
					}
				},
				{
					title: this.$t('Kopírovať skrátený'),
					description: this.$t('Text bude po vložení v riadkoch.'),
					image: copyText,
					handler: () => {
						this.onCopyPrintResult('txt_condense')
					}
				},
				{
					title: this.$t('Kopírovať ako CSV'),
					description: this.$t('Text bude po vložení vo formáte CSV.'),
					image: copyText,
					handler: () => {
						this.onCopyPrintResult('csv')
					}
				}
			]
		}
	},

	computed: {
		parameters() {
			if (!this.result?.params) return null

			let parameters = this._formatParameters(this.result.params, this.result.lab_id)
			if (this.result.lab_id == 'CYG') parameters = this._formatCygParameters(parameters)
			else if (this.result.lab_id == 'BPS') parameters = this._formatBpsParameters(parameters)
			else if (this.result.lab_id == 'NCY') parameters = this._formatNcyParameters(parameters)

			parameters = parameters.map((parameter) => {
				parameter._disabled_history = !parameter?.value?.value?.length || parameter?.value?.value == '... ' || parameter?.value?.type == 'TEXT' || parameter?.is_custom
				return parameter
			})

			return parameters

			return this.result.params.filter((parameter) => {
				const exclude = [
					// '80124', // Anamneza
					// '80123', // Klinicky nalez
					// '80121' // Cervikalna cytologia
				]

				return !exclude.includes(parameter.parameter_id)
			})
		},

		isPrintDownloadDisabled() {
			if (this.result?.result_id) return false
			return !this.parameters?.length
		},

		isSafari() {
			const ua = navigator.userAgent
			// Overenie, či ide o Safari (a nie Chrome alebo iný prehliadač)
			return ua.includes('Safari') && !ua.includes('Chrome')
		}
	},

	// 1130
	// - X odstranit 80061
	// - to kde je iba . mozeme odstranit uplne vsade, to su interne objednavky
	// - X konvencna cytlogia nazov zmenime za "LBC Cytologia", za tym je cislo vzdorky CLC24054782
	// - LIS by mal vracat poznamku administrativy a poznamku k ziadanke (ma byt na konci)

	// - X Na prve miesto 81002, odtial chceme iba "ina cytologia a cislo"
	// - X Na druhe miesto ide predmet vysetrenia aj s amb. oznacenim (z 81002), a pripadne aj dalsie
	// - X TNM klasifikacia premenovat na "Diagnoza patologa" (81168), skratka DG patologa

	// Biopsia - xxx41550
	// 84212 bude prve, BIOPSIA -> CLB24xxxx
	// X v419... -> vymazat (84401)
	// X 84212 -> Premenovat na predmet vysetrenia, vyhdoti prvu cast aby nebola duplicitne
	// X 84252 -> Ext. konz. BPS -> Externe konzultacne pracovisko, nalez zvyraznit, podciarknut a dat :
	// X 84313 -> NM klasifikacia premenovat na "Diagnoza patologa", skratka DG patologa

	methods: {
		_formatNcyParameters(parameters) {
			const newParameters = []

			parameters.forEach((parameter) => {
				const excludeParams = ['81152']
				if (excludeParams.includes(parameter.parameter_id)) {
					return
				} else if (parameter.parameter_id == '81002') {
					const value = parameter?.value?.value

					const inaCytologia = value?.match(/INÁ CYTOLÓGIA (.*?)\r\n/)?.[1]?.replace(/^\(|\)$/g, '') || null
					let predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*)/)?.[1]
					if (!predmetVysetrenia?.length) predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*)/)?.[1]

					newParameters.push({ name: 'Iná cytológia', value: { value: inaCytologia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					newParameters.push({ name: 'Predmet vyšetrenia', value: { value: predmetVysetrenia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })

					return
				} else if (parameter.parameter_id == '81168') {
					parameter.name = 'Diagnóza patológa'
					parameter.short_name = 'DG patológa'
				}

				return newParameters.push(parameter)
			})

			return newParameters
		},
		_formatBpsParameters(parameters) {
			const newParameters = []

			parameters.forEach((parameter) => {
				const excludeParams = ['84211', '84401']
				if (excludeParams.includes(parameter.parameter_id)) {
					return
				} else if (parameter.parameter_id == '84212') {
					const value = parameter?.value?.value

					const biopsia = value?.match(/BIOPSIA (.*?)\r\n/)?.[1]?.replace(/^\(|\)$/g, '') || null
					let predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*?)Populačný skríning/)?.[1]
					if (!predmetVysetrenia?.length) predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*)/)?.[1]

					newParameters.push({ name: 'Biopsia', value: { value: biopsia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					newParameters.push({ name: 'Predmet vyšetrenia', value: { value: predmetVysetrenia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })

					return
				} else if (parameter.parameter_id == '84525' && parameter?.value?.value) {
					parameter.value.value = parameter.value.value.replace('Ext.Konz.BPS:', 'Externé konzultačné pracovisko: ').replace('Nález\r\n', 'Nález:\r\n')
				} else if (parameter.parameter_id == '84313') {
					parameter.name = 'Diagnóza patológa'
					parameter.short_name = 'DG patológa'
				}

				return newParameters.push(parameter)
			})

			return newParameters
		},
		_formatCygParameters(parameters) {
			const newParameters = []

			const typeParam = parameters.find((parameter) => parameter.parameter_id == '80121')
			let type = 'KOC' // Konvencna cytologia

			if (typeParam?.value?.value.match(/LIQUID BASED CYTOLOGY/)) {
				type = 'LBC' // Liquid Based Cytology
			}

			parameters.forEach((parameter) => {
				if (parameter.parameter_id == '80052') {
					// Exclude Konvenčná cytológia
					return
				} else if (parameter.parameter_id == '80124') {
					// Exclude Anamneza, we will add it manually after Klinciky nalez
					return
				} else if (parameter.parameter_id == '80121') {
					// Format cervikalna cytologia
					const value = parameter?.value?.value

					let predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*?)Klinický nález/)?.[1]
					if (!predmetVysetrenia?.length) predmetVysetrenia = value?.match(/Predmet vyšetrenia([\s\S]*)/)?.[1]

					let klinickyNalez = value?.match(/Klinický nález([\s\S]*?)Anamnéza/)?.[1]?.replace(/, /g, '\r\n')
					if (!klinickyNalez?.length) klinickyNalez = value?.match(/Klinický nález([\s\S]*)/)?.[1]?.replace(/, /g, '\r\n')

					const anamneza = parameters.find((param) => param.parameter_id == '80124')

					if (type == 'KOC') {
						const konvencnaCytologia = value?.match(/KONVENČNÁ CYTOLÓGIA (.*?)\r\n/)?.[1]?.replace(/^\(|\)$/g, '') || null
						newParameters.push({ name: 'Konvenčná cytológia', value: { value: konvencnaCytologia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					} else if (type == 'LBC') {
						const lbcCytologia = value?.match(/LIQUID BASED CYTOLOGY (.*?)\r\n/)?.[1]?.replace(/^\(|\)$/g, '') || null
						newParameters.push({ name: 'LBC Cytológia', value: { value: lbcCytologia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					}

					newParameters.push({ name: 'Predmet vyšetrenia', value: { value: predmetVysetrenia, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					newParameters.push({ name: 'Klinický nález', value: { value: klinickyNalez, type: 'TEXT' }, is_custom: true, validate: parameter.validate })
					newParameters.push(anamneza)

					return
				} else if (parameter.parameter_id == '80007') {
					// Remove Doobjednávka if empty
					if (!parameter.value?.value) return
				} else if (parameter.parameter_id == '80131') {
					// Remove "VV" from Vysledok vysetrenia
					if (parameter.value?.value) parameter.value.value = parameter.value.value.replace('\r\nVV\r\n ', '')
				} else if (parameter.parameter_id == '80123') {
					// Remove default Klinicky nalez, we have it in cervikalna cytologia
					return
				} else if (parameter.parameter_id == '80061') {
					// Remove "LBC"
					return
				}

				return newParameters.push(parameter)
			})

			return newParameters
		},
		_formatParameters(params, lab_id) {
			const parameters = []

			params.forEach((parameter) => {
				const doobjednavkaCodes = ['80007', '81003']
				if (doobjednavkaCodes.includes(parameter.parameter_id)) {
					// Remove Doobjednávka if empty
					if (!parameter.value?.value) return
				} else if (parameter.name == 'Poznámka administratívy' && !['NCY', 'BPS', 'CYG'].includes(lab_id)) {
					parameter.name = this.$t('Poznámka k žiadanke')
					parameter.short_name = this.$t('Poznámka k žiadanke')
				}

				// Remove parameters with value "."
				if (parameter.value.value == '.') return

				return parameters.push(parameter)
			})

			return parameters
		},

		isCritical(val, val_min, val_max) {
			if (!val || !val_min || !val_max) return false

			return Number(val) < Number(val_min) || Number(val) > Number(val_max)
		},

		async showParameterHistory(parameter) {
			const queryObject = {
				test_id: parameter.parameter_id,
				birth_number: this.patient.personal_id,
				a_code: this.request.examination_request?.sending_doctor?.a_code || this.request.examination_request?.recommending_doctor?.a_code,
				p_code: this.request.examination_request?.sending_doctor?.p_code || this.request.examination_request?.recommending_doctor?.p_code,
				first_name: null,
				last_name: null
			}

			// We need to use first and last name for patients with personal_id ending with 0000 (generated patients)
			if (this.patient.personal_id.slice(-4) == '0000') {
				queryObject.first_name = this.patient.first_name
				queryObject.last_name = this.patient.last_name
			}

			const mdxParameterModal = await MdxParameterModal.create({
				componentProps: {
					// query: new URLSearchParams(queryObject).toString(),
					query: queryObject,
					parameter: parameter.parameter_data || parameter
				}
			})

			await mdxParameterModal.present()

			await mdxParameterModal.onDidDismiss()
		},

		async onCopyPrintResult(format = 'txt', action = 'copy') {
			if (!this.orderExternalId) return this.$toast.error('Nepodarilo sa získať ID objednávky')

			const orderExternalId = this.orderExternalId

			try {
				if (format == 'txt' || format == 'txt_condense' || format == 'csv') {
					this.loadingCopy = true
				} else if (format == 'pdf' && action == 'open') {
					this.loadingPdfOpen = true
				} else if (format == 'pdf' && action == 'download') {
					this.loadingPdfDownload = true
				}
				// CopyPrintMixin

				const resultIds = this.request.results.map((result) => result.full_result_id)

				let filename = `${this.patient.last_name || ''}_${this.patient.first_name || ''}_${this.orderExternalId}`
				filename = filename
					.replace(/[\s.]/g, '_')
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toLowerCase()

				if (format == 'csv') {
					const csvString = this.result?.params.map((obj) => `${obj?.short_name ? obj?.short_name : ''};${obj?.value?.value ? obj?.value?.value : this.$t('Neskôr')};${obj?.unit ? obj?.unit : ''}`).join('\n')

					await Clipboard.write({ string: csvString })

					this.$toast.info(this.$t('Výsledok bol skopírovaný'))
				} else {
					if (this.isSafari && action == 'copy') {
						const copyData = await this.copyResultData([this.result.full_result_id], orderExternalId, filename, format, action)
						const modal = await confirmModalController.create({
							text: 'Naozaj chcete skopírovať výsledok?',
							icon: copy,
							confirmText: 'Áno',
							confirmCallback: () => {
								this._resultDataAction(copyData, orderExternalId, filename, action)
							}
						})
						await modal.present()
					} else await this.copyPrintResult([this.result.full_result_id], this.orderExternalId, filename, format, action)
				}
			} catch (error) {
				console.error(error)
			} finally {
				if (format == 'txt' || format == 'txt_condense' || format == 'csv') {
					this.loadingCopy = false
				} else if (format == 'pdf' && action == 'open') {
					this.loadingPdfOpen = false
				} else if (format == 'pdf' && action == 'download') {
					this.loadingPdfDownload = false
				}
			}
		},

		formatParameterValueRichText(value) {
			if (!value) return this.$t('Neskôr')

			try {
				// Remove "\r", "\n", and " " from the start and end of the string
				value = value.replace(/^[\r\n\s]+|[\r\n\s]+$/g, '')

				// Replace non-breaking space with a regular space
				value = value.trim().replace(/\u{A0}/gu, '')

				let valueLines = value.split('\r\n')
				valueLines = valueLines.filter((line) => line.trim() !== '').map((line) => line.trim())
				// console.log(valueLines)

				valueLines = valueLines.map((line) => {
					// Wrap the text before each ":" with <strong>
					return line.replace(/(\b[^:]*):/g, '<strong style="text-decoration: underline">$1</strong>:')
				})

				// Join the lines back together with <br/> as the separator to keep the original functionality
				return valueLines.join('<br/>')
			} catch (error) {
				console.warn('Error while formatting rich text:', error)
				return value
			}
		}
	}
}
</script>

<style scoped lang="sass">
@import '@/plugins/app/theme/_mixins/responsive.mixin'
.container
	margin-bottom: 35px
	max-width: 100%

	&__header
		display: flex
		justify-content: space-between
		align-items: center
		margin-bottom: 18px

		+xl
			@apply block

		h1
			font-size: 18px
			font-weight: 700

		&__right
			@apply ml-[30px] relative

			+xl
				@apply ml-0

			+md
				@apply flex flex-col items-start

			&__btn
				@apply mr-[25px]

			span
				@apply inline-block

	&__table
		max-width: 100%
		overflow-x: auto

		table
			width: 100%
			border-collapse: collapse

			@apply text-sm

			thead tr
				background-color: #E6EBF0
				color: #6E7D8D
				font-size: 11px
				font-weight: 700
				line-height: 24px
				text-transform: uppercase

			th, td
				text-align: left
				padding: 8px 20px
				border-bottom: 1px solid #ddd
				height: 55px

			.approval
				padding: 8px 20px 8px 40px

			.right
				text-align: right

			.dotted-underline
				border-bottom: 1px dashed
				width: auto
				@apply border-gray-400
</style>
